import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ClearFiltersIcon from '@mui/icons-material/FilterAltOff';
import { useAccessControl } from '../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import { formatAccountingAmount } from '../../utils/formatters';
import { useAppSnackbar } from '../common/TableDesign';
import ModulePageHeader from '../common/ModulePageHeader';
import { EmptyRecordsState } from '../common/ResponsiveRecordList';
import {
  AccountClassPicker,
  AccountPicker,
  AccountTypePicker,
  CustomerPicker,
  DepartmentPicker,
  FiscalPeriodPicker,
  FiscalYearPicker,
  LocationPicker,
  SubTypePicker,
  SupplierPicker,
} from './LedgerPickers';
import { LedgerFields } from './LedgerFormDialog';
import TrialBalanceReport from './reports/TrialBalanceReport';
import BalanceSheetReport from './reports/BalanceSheetReport';
import IncomeStatementReport from './reports/IncomeStatementReport';

const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

const isPlainRecord = (v) => v && typeof v === 'object' && !Array.isArray(v);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const renderValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') {
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// ---------------------------------------------------------------------------
// Shared filter field building blocks - mirror the DTOs in
// accounting-apis financial-reporting/reports/dto exactly, so "every filter
// the backend accepts is reachable from the frontend" is literal, not
// aspirational. Grouped per report below into `scope` (which rows/accounts
// are included) and `display` (how the same rows are shaped/labelled).
// ---------------------------------------------------------------------------

const CLASS_TYPE_SUBTYPE_ACCOUNT = [
  { key: 'account_class_id', label: 'Account Class', type: 'picker', picker: AccountClassPicker },
  { key: 'account_type_id', label: 'Account Type', type: 'picker', picker: AccountTypePicker },
  { key: 'account_sub_type_id', label: 'Sub-Type', type: 'picker', picker: SubTypePicker },
  { key: 'account_id', label: 'Account', type: 'picker', picker: AccountPicker },
];

const GROUP_BY_OPTIONS = [
  { label: 'Class', value: 'CLASS' },
  { label: 'Type', value: 'TYPE' },
  { label: 'Sub-Type', value: 'SUB_TYPE' },
  { label: 'Flat (no grouping)', value: 'FLAT' },
];

const ENTRY_STATUS_OPTIONS = ['DRAFT', 'POSTED', 'APPROVED', 'VOID', 'REVERSED'].map((s) => ({ label: s, value: s }));

const withAllOption = (label, options) => [{ label: `All ${label}`, value: '' }, ...options];

// -- Balance Sheet --------------------------------------------------------

const BALANCE_SHEET_SCOPE = [
  ...CLASS_TYPE_SUBTYPE_ACCOUNT,
  { key: 'reporting_category', label: 'Reporting Category', type: 'select', options: withAllOption('categories', [{ label: 'Asset', value: 'asset' }, { label: 'Liability', value: 'liability' }, { label: 'Equity', value: 'equity' }]) },
  { key: 'as_of_date', label: 'As-of date (overrides period)', type: 'date' },
  { key: 'search', label: 'Search account code/name', type: 'text' },
];
const BALANCE_SHEET_DISPLAY = [
  { key: 'group_by', label: 'Group By', type: 'select', options: GROUP_BY_OPTIONS },
  { key: 'show_account_detail', label: 'Show account detail', type: 'switch', md: 4 },
  { key: 'show_sub_totals', label: 'Show sub-totals', type: 'switch', md: 4 },
  { key: 'include_zero_balances', label: 'Include zero balances', type: 'switch', md: 4 },
  { key: 'include_system_accounts', label: 'Include system accounts', type: 'switch', md: 4 },
  { key: 'include_inactive_accounts', label: 'Include inactive accounts', type: 'switch', md: 4 },
  { key: 'show_current_year_earnings', label: 'Show current-year earnings', type: 'switch', md: 4 },
  { key: 'show_retained_earnings_detail', label: 'Retained earnings detail', type: 'switch', md: 4 },
  { key: 'include_opening_balance_equity', label: 'Include opening balance equity', type: 'switch', md: 4 },
  { key: 'hide_immaterial', label: 'Hide immaterial balances', type: 'switch', md: 4 },
  { key: 'immaterial_pct', label: 'Immaterial threshold (%)', type: 'number', min: 0, md: 4 },
  { key: 'min_balance', label: 'Min balance', type: 'number', min: 0, md: 4 },
];
const BALANCE_SHEET_INITIAL = {
  reporting_category: '', as_of_date: '', search: '',
  group_by: 'TYPE', show_account_detail: true, show_sub_totals: true,
  include_zero_balances: true, include_system_accounts: true, include_inactive_accounts: false,
  show_current_year_earnings: true, show_retained_earnings_detail: false, include_opening_balance_equity: true,
  hide_immaterial: false, immaterial_pct: 1, min_balance: '',
};

// -- Income Statement -------------------------------------------------------

const INCOME_STATEMENT_SCOPE = [
  ...CLASS_TYPE_SUBTYPE_ACCOUNT,
  { key: 'reporting_category', label: 'Reporting Category', type: 'select', options: withAllOption('categories', [{ label: 'Income', value: 'income' }, { label: 'Expense', value: 'expense' }]) },
  { key: 'department_id', label: 'Department', type: 'picker', picker: DepartmentPicker },
  { key: 'location_id', label: 'Location', type: 'picker', picker: LocationPicker },
  { key: 'project_id', label: 'Project ID', type: 'number', min: 1 },
  { key: 'date_from', label: 'Date from (overrides period)', type: 'date' },
  { key: 'date_to', label: 'Date to', type: 'date' },
  { key: 'ytd', label: 'Year to date', type: 'switch', md: 6 },
];
const INCOME_STATEMENT_DISPLAY = [
  { key: 'group_by', label: 'Group By', type: 'select', options: GROUP_BY_OPTIONS },
  { key: 'show_account_detail', label: 'Show account detail', type: 'switch', md: 4 },
  { key: 'show_sub_totals', label: 'Show sub-totals', type: 'switch', md: 4 },
  { key: 'show_gross_profit', label: 'Show gross profit', type: 'switch', md: 4 },
  { key: 'show_ebitda', label: 'Show EBITDA', type: 'switch', md: 4 },
  { key: 'include_zero_balances', label: 'Include zero balances', type: 'switch', md: 4 },
  { key: 'include_system_accounts', label: 'Include system accounts', type: 'switch', md: 4 },
  { key: 'hide_immaterial', label: 'Hide immaterial amounts', type: 'switch', md: 4 },
  { key: 'immaterial_pct', label: 'Immaterial threshold (%)', type: 'number', min: 0, md: 4 },
  { key: 'min_amount', label: 'Min amount', type: 'number', min: 0, md: 4 },
];
const INCOME_STATEMENT_INITIAL = {
  reporting_category: '', department_id: '', location_id: '', project_id: '', date_from: '', date_to: '', ytd: false,
  group_by: 'TYPE', show_account_detail: false, show_sub_totals: true, show_gross_profit: true, show_ebitda: false,
  include_zero_balances: false, include_system_accounts: true, hide_immaterial: false, immaterial_pct: 1, min_amount: '',
};

// -- General Ledger -----------------------------------------------------------

const GL_SCOPE = [
  ...CLASS_TYPE_SUBTYPE_ACCOUNT,
  { key: 'financial_statement_type', label: 'Statement Type', type: 'select', options: withAllOption('statements', [{ label: 'Balance Sheet', value: 'balance_sheet' }, { label: 'Income Statement', value: 'income_statement' }]) },
  { key: 'reporting_category', label: 'Reporting Category', type: 'select', options: withAllOption('categories', ['asset', 'liability', 'equity', 'income', 'expense'].map((v) => ({ label: v, value: v }))) },
  { key: 'entry_status', label: 'Entry Status', type: 'select', options: ENTRY_STATUS_OPTIONS },
  { key: 'entry_type', label: 'Entry Type', type: 'text' },
  { key: 'reference', label: 'Reference contains', type: 'text' },
  { key: 'source_module', label: 'Source Module', type: 'text' },
  { key: 'department_id', label: 'Department', type: 'picker', picker: DepartmentPicker },
  { key: 'location_id', label: 'Location', type: 'picker', picker: LocationPicker },
  { key: 'project_id', label: 'Project ID', type: 'number', min: 1 },
  { key: 'customer_id', label: 'Customer', type: 'picker', picker: CustomerPicker },
  { key: 'supplier_id', label: 'Supplier', type: 'picker', picker: SupplierPicker },
  { key: 'date_from', label: 'Date from (overrides period)', type: 'date' },
  { key: 'date_to', label: 'Date to', type: 'date' },
  { key: 'search', label: 'Search', type: 'text' },
];
const GL_DISPLAY = [
  { key: 'sort_by', label: 'Sort By', type: 'select', options: [{ label: 'Date', value: 'date' }, { label: 'Entry Number', value: 'entry_number' }, { label: 'Account Code', value: 'account_code' }] },
  { key: 'group_by', label: 'Group By', type: 'select', options: [{ label: 'Account', value: 'account' }, { label: 'Period', value: 'period' }, { label: 'Flat', value: 'flat' }] },
  { key: 'page_size', label: 'Page Size', type: 'number', min: 1 },
  { key: 'include_system_accounts', label: 'Include system accounts', type: 'switch', md: 6 },
  { key: 'include_inactive_accounts', label: 'Include inactive accounts', type: 'switch', md: 6 },
  { key: 'include_opening_balance', label: 'Include opening balance', type: 'switch', md: 6 },
  { key: 'show_running_balance', label: 'Show running balance', type: 'switch', md: 6 },
];
const GL_INITIAL = {
  financial_statement_type: '', reporting_category: '', entry_status: 'POSTED', entry_type: '', reference: '',
  source_module: '', department_id: '', location_id: '', project_id: '', customer_id: '', supplier_id: '',
  date_from: '', date_to: '', search: '',
  sort_by: 'date', group_by: 'account', page_size: 100,
  include_system_accounts: true, include_inactive_accounts: false, include_opening_balance: true, show_running_balance: true,
};

// -- Trial Balance --------------------------------------------------------

const TRIAL_BALANCE_SCOPE = [
  ...CLASS_TYPE_SUBTYPE_ACCOUNT,
  { key: 'financial_statement_type', label: 'Statement Type', type: 'select', options: withAllOption('statements', [{ label: 'Balance Sheet', value: 'balance_sheet' }, { label: 'Income Statement', value: 'income_statement' }]) },
  { key: 'reporting_category', label: 'Reporting Category', type: 'select', options: withAllOption('categories', ['asset', 'liability', 'equity', 'income', 'expense'].map((v) => ({ label: v, value: v }))) },
  { key: 'cash_flow_type', label: 'Cash Flow Type', type: 'select', options: withAllOption('types', ['operating', 'investing', 'financing', 'not_applicable'].map((v) => ({ label: v.replace(/_/g, ' '), value: v }))) },
  { key: 'as_of_date', label: 'As-of date (overrides period)', type: 'date' },
  { key: 'search', label: 'Search account code/name', type: 'text' },
];
const TRIAL_BALANCE_DISPLAY = [
  { key: 'group_by', label: 'Group By', type: 'select', options: GROUP_BY_OPTIONS },
  { key: 'sort_by', label: 'Sort By', type: 'select', options: [{ label: 'Account Code', value: 'account_code' }, { label: 'Account Name', value: 'account_name' }, { label: 'Balance (desc)', value: 'balance_desc' }, { label: 'Balance (asc)', value: 'balance_asc' }] },
  { key: 'balance_side', label: 'Balance Side', type: 'select', options: [{ label: 'Both', value: 'both' }, { label: 'Debit only', value: 'debit_only' }, { label: 'Credit only', value: 'credit_only' }] },
  { key: 'account_status', label: 'Account Status', type: 'select', options: ['ACTIVE', 'INACTIVE', 'SUSPENDED', 'CLOSED'].map((v) => ({ label: v, value: v })) },
  { key: 'allow_posting', label: 'Posting Allowed', type: 'select', options: [{ label: 'Any', value: '' }, { label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }] },
  { key: 'min_balance', label: 'Min balance', type: 'number', min: 0 },
  { key: 'include_zero_balances', label: 'Include zero balances', type: 'switch', md: 6 },
  { key: 'include_system_accounts', label: 'Include system accounts', type: 'switch', md: 6 },
  { key: 'show_sub_totals', label: 'Show sub-totals', type: 'switch', md: 6 },
];
const TRIAL_BALANCE_INITIAL = {
  financial_statement_type: '', reporting_category: '', cash_flow_type: '', as_of_date: '', search: '',
  group_by: 'CLASS', sort_by: 'account_code', balance_side: 'both', account_status: 'ACTIVE', allow_posting: '',
  min_balance: '', include_zero_balances: true, include_system_accounts: true, show_sub_totals: true,
};

// -- Cash Flow --------------------------------------------------------------

const CASH_FLOW_SCOPE = [
  { key: 'date_from', label: 'Date from (overrides period)', type: 'date' },
  { key: 'date_to', label: 'Date to', type: 'date' },
  { key: 'ytd', label: 'Year to date', type: 'switch', md: 6 },
];
const CASH_FLOW_DISPLAY = [
  { key: 'method', label: 'Method', type: 'select', options: [{ label: 'Indirect', value: 'indirect' }, { label: 'Direct', value: 'direct' }] },
  { key: 'group_by', label: 'Group By', type: 'select', options: GROUP_BY_OPTIONS },
  { key: 'show_account_detail', label: 'Show account detail', type: 'switch', md: 4 },
  { key: 'show_sub_totals', label: 'Show sub-totals', type: 'switch', md: 4 },
  { key: 'show_working_capital_detail', label: 'Working capital detail', type: 'switch', md: 4 },
  { key: 'include_zero_balances', label: 'Include zero balances', type: 'switch', md: 4 },
  { key: 'include_system_accounts', label: 'Include system accounts', type: 'switch', md: 4 },
  { key: 'hide_immaterial', label: 'Hide immaterial amounts', type: 'switch', md: 4 },
  { key: 'immaterial_pct', label: 'Immaterial threshold (%)', type: 'number', min: 0, md: 4 },
  { key: 'min_amount', label: 'Min amount', type: 'number', min: 0, md: 4 },
];
const CASH_FLOW_INITIAL = {
  date_from: '', date_to: '', ytd: false,
  method: 'indirect', group_by: 'TYPE', show_account_detail: true, show_sub_totals: true,
  show_working_capital_detail: true, include_zero_balances: false, include_system_accounts: true,
  hide_immaterial: false, immaterial_pct: 1, min_amount: '',
};

// ---------------------------------------------------------------------------

// Each report's own fiscal-period query param name - they don't all agree
// (accounting-apis' BalanceSheetQueryDto uses `as_of_period_id`, everything
// else uses `period_id`). Getting this wrong doesn't error, it just makes
// the period filter silently do nothing.
const REPORTS = [
  {
    value: 'trial-balance', label: 'Trial Balance', fn: (params) => ledgerAPI.getReportTrialBalance(params),
    exportFn: (params) => ledgerAPI.exportTrialBalance(params), periodParam: 'period_id', supportsCompare: false,
    scope: TRIAL_BALANCE_SCOPE, display: TRIAL_BALANCE_DISPLAY, initial: TRIAL_BALANCE_INITIAL,
  },
  {
    value: 'balance-sheet', label: 'Balance Sheet', fn: (params) => ledgerAPI.getBalanceSheet(params),
    exportFn: (params) => ledgerAPI.exportBalanceSheet(params), periodParam: 'as_of_period_id', supportsCompare: true,
    scope: BALANCE_SHEET_SCOPE, display: BALANCE_SHEET_DISPLAY, initial: BALANCE_SHEET_INITIAL,
  },
  {
    value: 'income-statement', label: 'Income Statement', fn: (params) => ledgerAPI.getIncomeStatement(params),
    exportFn: (params) => ledgerAPI.exportIncomeStatement(params), periodParam: 'period_id', supportsCompare: true,
    scope: INCOME_STATEMENT_SCOPE, display: INCOME_STATEMENT_DISPLAY, initial: INCOME_STATEMENT_INITIAL,
  },
  {
    value: 'general-ledger', label: 'General Ledger', fn: (params) => ledgerAPI.getGeneralLedgerReport(params),
    exportFn: (params) => ledgerAPI.exportGeneralLedger(params), periodParam: 'period_id', supportsCompare: false,
    scope: GL_SCOPE, display: GL_DISPLAY, initial: GL_INITIAL,
  },
  {
    value: 'cash-flow', label: 'Cash Flow', fn: (params) => ledgerAPI.getCashFlow(params),
    periodParam: 'period_id', supportsCompare: true,
    scope: CASH_FLOW_SCOPE, display: CASH_FLOW_DISPLAY, initial: CASH_FLOW_INITIAL,
  },
  {
    value: 'changes-in-equity', label: 'Changes in Equity', fn: (params) => ledgerAPI.getChangesInEquity(params),
    periodParam: null, supportsCompare: false,
    scope: [], display: [], initial: {},
  },
];

const STRUCTURED_REPORTS = {
  'trial-balance': TrialBalanceReport,
  'balance-sheet': BalanceSheetReport,
  'income-statement': IncomeStatementReport,
};

// Boolean/number/select fields always ride along once set (a `false` switch
// or `0` threshold is meaningful); only truly-empty text/picker/select
// values are dropped so the backend falls back to its own default.
const cleanParams = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === '' || v === null || v === undefined) return;
    out[k] = v;
  });
  if (out.allow_posting === 'true') out.allow_posting = true;
  else if (out.allow_posting === 'false') out.allow_posting = false;
  return out;
};

/** Fallback for report types that don't have a dedicated layout yet
 * (Cash Flow, Changes in Equity) - renders whatever comes back as a
 * key/value summary plus an auto-columned table. */
const GenericReportTable = ({ label, data, loading }) => {
  if (loading) {
    return <Typography color="text.secondary">Loading report...</Typography>;
  }
  const rows = Array.isArray(data) ? data : data?.items || data?.rows || [];
  const summary = !Array.isArray(data) && isPlainRecord(data)
    ? Object.entries(data).filter(([k]) => !['items', 'rows', 'data'].includes(k))
    : [];
  const columns = rows.length > 0 ? Object.keys(rows[0]).filter((k) => !k.startsWith('_')) : [];

  if (rows.length === 0 && summary.length === 0) {
    return <EmptyRecordsState message="No data returned for this report." />;
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>{label}</Typography>
      {summary.length > 0 && (
        <Box mb={2}>
          {summary.map(([k, v]) => (
            <Box key={k} display="flex" justifyContent="space-between" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">{k.replace(/_/g, ' ')}</Typography>
              <Typography variant="body2" fontWeight={700}>{renderValue(v)}</Typography>
            </Box>
          ))}
        </Box>
      )}
      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c} sx={{ fontWeight: 700 }}>{c.replace(/_/g, ' ')}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => (
                <TableRow key={i} hover>
                  {columns.map((c) => (
                    <TableCell key={c} align={typeof row[c] === 'number' ? 'right' : 'left'}>
                      {renderValue(row[c])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </>
  );
};

/** GL report table with per-account drilldown into the account ledger. */
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// "Particulars" (classic ledger-book term for the counterpart of an entry):
// prefers the actual other-side account(s) on the same journal entry (only
// populated on the account-ledger drilldown, which resolves them); falls
// back to source module/entry type, matching how the GL report's own lines
// (no split-account data) still show something useful.
const formatSplitAccounts = (splits = []) => {
  const seen = new Set();
  return splits
    .map((a) => `${a.account_code} - ${a.account_name}`)
    .filter((label) => { if (seen.has(label)) return false; seen.add(label); return true; })
    .join('; ');
};
const getParticulars = (line) => {
  const cp = formatSplitAccounts([...(line.debit_accounts || []), ...(line.credit_accounts || [])]);
  if (cp) return cp;
  return line.source_module || line.entry_type || '';
};

/** One account's transaction block: collapsed to a summary row by default,
 * expands to a classic ledger layout (Opening Balance / lines / Total /
 * Closing Balance) - account is the parent, its journal lines are the
 * children. Mirrors accounting-apis GeneralLedgerService's own shape
 * (account.lines[]), so no separate fetch is needed to expand a row. */
const GlAccountBlock = ({ account, onOpenDialog }) => {
  const [expanded, setExpanded] = useState(false);
  const lines = account.lines ?? [];
  const showParty = lines.some((l) => l.customer_name || l.supplier_name);
  const detailColSpan = 5 + (showParty ? 1 : 0);

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer', '& td': { borderBottom: expanded ? 0 : undefined } }} onClick={() => setExpanded((v) => !v)}>
        <TableCell sx={{ fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
              {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
            <Box>
              <Typography variant="body2" fontWeight={700}>{account.account_code} - {account.account_name}</Typography>
              {(account.class_name || account.type_name || account.sub_type_name) && (
                <Typography variant="caption" color="text.secondary">
                  {[account.class_name, account.type_name, account.sub_type_name].filter(Boolean).join(' · ')}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.closing_debit)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.closing_credit)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.closing_balance)}</TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Open in dialog">
            <IconButton size="small" onClick={() => onOpenDialog(account.account_id, `${account.account_code} - ${account.account_name}`)}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
              {lines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No transactions in this period.</Typography>
              ) : (
                <Table size="small" sx={{ bgcolor: 'background.paper' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Entry #</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Particulars</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Ref</TableCell>
                      {showParty && <TableCell sx={{ fontWeight: 600 }}>Customer/Supplier</TableCell>}
                      <TableCell sx={{ fontWeight: 600 }} align="right">Debit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Credit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={detailColSpan} sx={{ fontWeight: 600, color: 'text.secondary', fontStyle: 'italic' }}>Opening Balance</TableCell>
                      <TableCell align="right" />
                      <TableCell align="right" />
                      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.opening_balance)}</TableCell>
                    </TableRow>
                    {lines.map((l) => (
                      <TableRow key={l.line_id} hover>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{fmtDate(l.entry_date)}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{l.entry_number}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>{getParticulars(l)}</Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{l.description || ''}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem' }}>{l.reference || ''}</TableCell>
                        {showParty && <TableCell sx={{ fontSize: '0.82rem' }}>{l.customer_name || l.supplier_name || ''}</TableCell>}
                        <TableCell align="right" sx={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(l.debit_amount, { blankZero: true })}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(l.credit_amount, { blankZero: true })}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.82rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                          {l.running_balance !== undefined ? formatAccountingAmount(l.running_balance) : ''}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={detailColSpan} sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.period_debit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.period_credit)}</TableCell>
                      <TableCell />
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'action.selected' }}>
                      <TableCell colSpan={detailColSpan} sx={{ fontWeight: 700 }}>Closing Balance</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.closing_debit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.closing_credit)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(account.closing_balance)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/** General Ledger report: one collapsible block per account (parent), each
 * expanding to its own journal lines (children) - see GlAccountBlock. */
const GlHierarchyTable = ({ data, loading, onDrilldown }) => {
  if (loading) return <Typography color="text.secondary">Generating general ledger report...</Typography>;
  const accounts = Array.isArray(data) ? data : data?.accounts ?? data?.items ?? data?.rows ?? [];
  if (!accounts.length) return <EmptyRecordsState message="No data returned for this report." />;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <Typography variant="h6">General Ledger</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {data?.period_name && <Chip label={data.period_name} size="small" variant="outlined" />}
          {data?.total_accounts !== undefined && (
            <Typography variant="caption" color="text.secondary">{data.total_accounts} account(s)</Typography>
          )}
        </Stack>
      </Stack>
      <Table size="small" sx={{ border: '1px solid', borderColor: 'divider' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ fontWeight: 700 }}>Account</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Closing Debit</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Closing Credit</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Closing Balance</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((a) => (
            <GlAccountBlock key={a.account_id} account={a} onOpenDialog={onDrilldown} />
          ))}
        </TableBody>
        {data?.total_closing_debit !== undefined && (
          <TableBody>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'primary.contrastText', fontWeight: 700 }}>Grand Total</TableCell>
              <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(data.total_closing_debit)}</TableCell>
              <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(data.total_closing_credit)}</TableCell>
              <TableCell align="right" sx={{ color: 'primary.contrastText', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatAccountingAmount(num(data.total_closing_debit) - num(data.total_closing_credit))}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        )}
      </Table>
    </Box>
  );
};

// Export failures still come back as JSON from the Laravel gateway, but
// axios hands them over as a Blob (responseType is fixed per-request) -
// read it back out to get the real message instead of a generic one.
const readBlobError = async (error, fallback) => {
  const blob = error?.response?.data;
  if (blob instanceof Blob && blob.type.includes('json')) {
    try {
      const text = await blob.text();
      const json = JSON.parse(text);
      return json?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return getErrorMessage(error, fallback);
};

const downloadBlobResponse = (response, filename) => {
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const LedgerReports = () => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const [report, setReport] = useState(REPORTS[0]);
  const [fiscalYearId, setFiscalYearId] = useState('');
  const [periodId, setPeriodId] = useState('');
  const [comparePeriodId, setComparePeriodId] = useState('');
  const [compareFiscalYearId, setCompareFiscalYearId] = useState('');
  const [filters, setFilters] = useState(REPORTS[0].initial);
  const [showFilters, setShowFilters] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodReady, setPeriodReady] = useState(false);
  const [exporting, setExporting] = useState(null);
  // GL account-ledger drilldown
  const [drilldown, setDrilldown] = useState({ open: false, account: null, rows: [], loading: false, opening: 0, closing: 0, totalDebits: 0, totalCredits: 0 });

  useEffect(() => {
    ledgerAPI.getCurrentFiscalPeriod()
      .then((res) => {
        const period = res?.data?.data ?? res?.data ?? null;
        const id = period?.period_id ?? period?.id ?? null;
        const yearId = period?.fiscal_year_id ?? null;
        if (id) setPeriodId(id);
        if (yearId) setFiscalYearId(yearId);
      })
      .catch(() => {})
      .finally(() => setPeriodReady(true));
  }, []);

  const hasDateRange = filters.date_from && filters.date_to;
  const hasAsOfDate = Boolean(filters.as_of_date);
  const ready = report.value === 'changes-in-equity'
    ? Boolean(fiscalYearId)
    : Boolean(periodId) || hasDateRange || hasAsOfDate;

  const buildParams = useCallback(() => {
    if (report.value === 'changes-in-equity') {
      return { fiscal_year_id: fiscalYearId };
    }
    const params = {};
    if (periodId) params[report.periodParam] = periodId;
    if (report.supportsCompare && comparePeriodId) params.comparison_period_id = comparePeriodId;
    if (report.supportsCompare && compareFiscalYearId) params.comparison_fiscal_year_id = compareFiscalYearId;
    return { ...params, ...cleanParams(filters) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, periodId, fiscalYearId, comparePeriodId, compareFiscalYearId, filters]);

  const load = useCallback(async () => {
    if (!can(RESOURCES.LEDGER.REPORTS, ACTIONS.READ)) return;
    if (!ready) return;
    try {
      setLoading(true);
      const response = await report.fn(buildParams());
      setData(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      setData(null);
      showSnackbar(getErrorMessage(error, 'Failed to fetch report'), 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, ready, buildParams]);

  useEffect(() => { if (periodReady) load(); }, [load, periodReady]);

  // When fiscal year changes, clear period so user picks one from the new year
  const handleYearChange = (id) => {
    setFiscalYearId(id);
    setPeriodId('');
    setComparePeriodId('');
    setCompareFiscalYearId('');
    setData(null);
  };

  const handleReportChange = (value) => {
    const next = REPORTS.find((r) => r.value === value);
    if (!next) return;
    setReport(next);
    setFilters(next.initial);
    setComparePeriodId('');
    setCompareFiscalYearId('');
    setData(null);
  };

  const clearFilters = () => setFilters(report.initial);
  const hasActiveFilters = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(report.initial),
    [filters, report]
  );

  const runExport = async (format) => {
    try {
      setExporting(format);
      const response = await report.exportFn({ ...buildParams(), format });
      const ext = format === 'xlsx' ? 'xlsx' : 'csv';
      downloadBlobResponse(response, `${report.value}-${new Date().toISOString().slice(0, 10)}.${ext}`);
    } catch (error) {
      showSnackbar(await readBlobError(error, 'Export failed'), 'error');
    } finally {
      setExporting(null);
    }
  };

  // GL report: open account-ledger drilldown for a row. The account-ledger
  // endpoint returns { ...header, transactions, page, limit, total } - not
  // the {items} / {entries} shape most other list endpoints use - reading
  // the wrong key here silently produced an empty dialog even for accounts
  // with real activity.
  const openDrilldown = async (accountId, accountLabel) => {
    if (!accountId) return;
    setDrilldown({ open: true, account: accountLabel, rows: [], loading: true, opening: 0, closing: 0, totalDebits: 0, totalCredits: 0 });
    try {
      const res = await ledgerAPI.getAccountLedger(accountId, { period_id: periodId, limit: 200 });
      const payload = res?.data?.data ?? res?.data ?? {};
      const rows = Array.isArray(payload) ? payload : payload.transactions ?? [];
      setDrilldown((p) => ({
        ...p,
        rows,
        loading: false,
        opening: num(payload.opening_balance),
        closing: num(payload.closing_balance),
        totalDebits: num(payload.total_debits),
        totalCredits: num(payload.total_credits),
      }));
    } catch (error) {
      setDrilldown((p) => ({ ...p, loading: false }));
      showSnackbar(getErrorMessage(error, 'Failed to fetch account ledger'), 'error');
    }
  };

  if (!can(RESOURCES.LEDGER.REPORTS, ACTIONS.READ)) {
    return <Alert severity="error">You do not have permission to view financial reports.</Alert>;
  }

  const StructuredComponent = STRUCTURED_REPORTS[report.value];
  const filterCount = Object.entries(filters).filter(([k, v]) => {
    const initial = report.initial[k];
    return v !== initial && v !== '' && v !== null && v !== undefined;
  }).length;

  return (
    <Box>
      <ModulePageHeader title="Financial Reports" subtitle="Live reports from the accounting ledger" />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              size="small"
              options={REPORTS}
              getOptionLabel={(o) => o.label}
              value={report}
              onChange={(_, value) => { if (value) handleReportChange(value.value); }}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              renderInput={(params) => <TextField {...params} label="Report" />}
              sx={{ minWidth: 220 }}
              disableClearable
            />
            <Box sx={{ minWidth: 190 }}>
              <FiscalYearPicker label="Fiscal Year" value={fiscalYearId} onChange={handleYearChange} />
            </Box>
            {report.periodParam && (
              <Box sx={{ minWidth: 210 }}>
                <FiscalPeriodPicker
                  label={report.value === 'balance-sheet' ? 'As-of Period' : 'Period'}
                  value={periodId}
                  onChange={(id) => setPeriodId(id)}
                  yearId={fiscalYearId || undefined}
                />
              </Box>
            )}
            {report.supportsCompare && (
              <>
                <Box sx={{ minWidth: 210 }}>
                  <FiscalPeriodPicker
                    label="Compare to period (optional)"
                    value={comparePeriodId}
                    onChange={(id) => setComparePeriodId(id)}
                    yearId={fiscalYearId || undefined}
                  />
                </Box>
                <Box sx={{ minWidth: 190 }}>
                  <FiscalYearPicker label="Compare to year (optional)" value={compareFiscalYearId} onChange={setCompareFiscalYearId} />
                </Box>
              </>
            )}
            <Button
              size="small"
              variant={showFilters ? 'contained' : 'outlined'}
              startIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowFilters((s) => !s)}
              disabled={report.scope.length === 0 && report.display.length === 0}
            >
              Filters{filterCount > 0 ? ` (${filterCount})` : ''}
            </Button>
            {hasActiveFilters && (
              <Button size="small" variant="outlined" color="inherit" startIcon={<ClearFiltersIcon />} onClick={clearFilters}>
                Clear filters
              </Button>
            )}
            {report.exportFn && (
              <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!ready || exporting} onClick={() => runExport('csv')}>
                  {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!ready || exporting} onClick={() => runExport('xlsx')}>
                  {exporting === 'xlsx' ? 'Exporting...' : 'Export XLSX'}
                </Button>
              </Stack>
            )}
          </Box>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              {report.scope.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Scope</Typography>
                  <LedgerFields
                    fields={report.scope}
                    values={filters}
                    onChange={(key, value) => setFilters((p) => ({ ...p, [key]: value }))}
                    sx={{ mb: report.display.length > 0 ? 2 : 0 }}
                  />
                </>
              )}
              {report.display.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>Display options</Typography>
                  <LedgerFields
                    fields={report.display}
                    values={filters}
                    onChange={(key, value) => setFilters((p) => ({ ...p, [key]: value }))}
                  />
                </>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {!ready ? (
            <EmptyRecordsState message={report.value === 'changes-in-equity' ? 'Choose a fiscal year to generate this report.' : 'Choose a fiscal period to generate this report.'} />
          ) : StructuredComponent ? (
            <StructuredComponent data={data} loading={loading} />
          ) : report.value === 'general-ledger' ? (
            <GlHierarchyTable data={data} loading={loading} onDrilldown={openDrilldown} />
          ) : (
            <GenericReportTable label={report.label} data={data} loading={loading} />
          )}
        </CardContent>
      </Card>

      {/* GL account-ledger drilldown dialog - kept as a focused single-account
          popup alongside the inline hierarchy above; this one resolves real
          counterpart accounts for Particulars (see journal-entry.repository
          ::getAccountLedger), which the GL report's own lines don't carry. */}
      <Dialog open={drilldown.open} onClose={() => setDrilldown((p) => ({ ...p, open: false }))} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Account Ledger</Typography>
            <Typography variant="body2" color="text.secondary">{drilldown.account}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setDrilldown((p) => ({ ...p, open: false }))}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {drilldown.loading ? (
            <Box py={4} textAlign="center"><Typography color="text.secondary">Loading…</Typography></Box>
          ) : drilldown.rows.length === 0 ? (
            <Box py={4} textAlign="center"><Typography color="text.secondary">No entries for this period.</Typography></Box>
          ) : (() => {
            const showParty = drilldown.rows.some((r) => r.customer_name || r.supplier_name);
            return (
              <Box>
                <Stack direction="row" flexWrap="wrap" divider={<Box sx={{ width: '1px', bgcolor: 'divider' }} />} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                  {[
                    ['Opening', drilldown.opening],
                    ['Total Debit', drilldown.totalDebits],
                    ['Total Credit', drilldown.totalCredits],
                    ['Closing', drilldown.closing],
                  ].map(([label, value]) => (
                    <Box key={label} sx={{ flex: '1 1 140px', textAlign: 'center', py: 1.5, px: 1 }}>
                      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(value)}</Typography>
                    </Box>
                  ))}
                </Stack>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Entry #</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Particulars</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                        {showParty && <TableCell sx={{ fontWeight: 700 }}>Customer/Supplier</TableCell>}
                        <TableCell sx={{ fontWeight: 700 }} align="right">Debit</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Credit</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {drilldown.rows.map((r, i) => (
                        <TableRow key={r.line_id ?? i} hover>
                          <TableCell>{fmtDate(r.entry_date ?? r.date)}</TableCell>
                          <TableCell>{r.entry_number ?? ''}</TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">{getParticulars(r)}</Typography>
                          </TableCell>
                          <TableCell>{r.description ?? r.narration ?? ''}</TableCell>
                          <TableCell>{r.reference ?? ''}</TableCell>
                          {showParty && <TableCell>{r.customer_name || r.supplier_name || ''}</TableCell>}
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatAccountingAmount(r.debit_amount, { blankZero: true })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatAccountingAmount(r.credit_amount, { blankZero: true })}
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                            {r.running_balance !== undefined ? formatAccountingAmount(r.running_balance) : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {SnackbarComponent}
    </Box>
  );
};

export default LedgerReports;
